import {
  Descriptions,
  PrimaryButton,
  Space,
  StatusBadge,
  Text,
  TextLabel,
} from '@procurify/ui'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import messages from './messages'

export interface IStatementDetailsPanelProps {
  account: CreditCardAccount
  onReconcileClick: (account: CreditCardAccount) => void
}

export const StatementDetailsPanel = ({
  account,
  onReconcileClick,
}: IStatementDetailsPanelProps) => {
  const intl = useProcurifyIntl()

  const session = account.session

  // Status action branch (BHV-009): no session → Reconcile; DRAFT →
  // Continue reconciliation; SUBMITTED → bill link.
  const renderStatusAction = () => {
    if (session && account.hasSubmittedSession && session.billUuid) {
      return (
        <BridgeLink to={`/#/bills/cc-bill/${session.billUuid}`}>
          {session.billNumberLabel}
        </BridgeLink>
      )
    }

    return (
      <PrimaryButton type='button' onClick={() => onReconcileClick(account)}>
        {account.hasDraftSession
          ? intl.formatMessage(messages.continueReconciliationButton)
          : intl.formatMessage(messages.reconcileButton)}
      </PrimaryButton>
    )
  }

  const renderStatusBadge = () => {
    if (!session) {
      return (
        <StatusBadge
          type='inactive'
          text={intl.formatMessage(messages.openStatus)}
        />
      )
    }

    if (account.hasSubmittedSession) {
      return (
        <StatusBadge
          type='success'
          text={intl.formatMessage(messages.submittedStatus)}
        />
      )
    }

    return (
      <StatusBadge
        type='warning'
        text={intl.formatMessage(messages.draftStatus)}
      />
    )
  }

  return (
    <Space>
      <Text bold autoMargin={false}>
        {intl.formatMessage(messages.panelTitle)}
      </Text>
      <Space direction='horizontal' justify='space-between' align='start'>
        <Space direction='horizontal' size='large'>
          <Space.Item minWidth={200}>
            <Descriptions columns={1}>
              <Descriptions.Item
                label={intl.formatMessage(messages.lastStatementDateLabel)}
              >
                {account.lastReconciliationDate ? (
                  <FormattedShortDate date={account.lastReconciliationDate} />
                ) : (
                  '--'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Space.Item>
          <Space.Item minWidth={200}>
            <Descriptions columns={1}>
              <Descriptions.Item
                label={intl.formatMessage(messages.currentStatementDateLabel)}
              >
                {account.currentStatementDate ? (
                  <FormattedShortDate date={account.currentStatementDate} />
                ) : (
                  '--'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Space.Item>
          <Space.Item minWidth={200}>
            <Descriptions columns={1}>
              <Descriptions.Item
                label={intl.formatMessage(messages.lastStatementBalanceLabel)}
              >
                <Text bold autoMargin={false}>
                  <Price
                    price={account.lastReconciliationBalance ?? 0}
                    currency={account.currency.name}
                    inline
                  />
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Space.Item>
        </Space>

        <Space size='small' align='end'>
          {renderStatusAction()}
          <Space direction='horizontal' size='small' align='center'>
            <TextLabel>{intl.formatMessage(messages.statusLabel)}</TextLabel>
            {renderStatusBadge()}
          </Space>
        </Space>
      </Space>
    </Space>
  )
}

StatementDetailsPanel.displayName = 'StatementDetailsPanel'
